import { MONTH_ABBREVIATIONS, STATUS_CLASSES } from '../constants/A3Constants';

/**
 * Generates a unique ID with optional prefix
 * @param prefix - Optional prefix for the ID
 * @returns Unique ID string
 */
export const genId = (prefix = ''): string => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10000);
  const suffix = prefix ? `-${prefix}` : '';
  return `${timestamp}-${random}${suffix}`;
};

/**
 * Formats a date string or converts ISO date to readable format
 * @param dateString - Date string in various formats
 * @returns Formatted date string (e.g., "17-Aug-2026")
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  // Check if it's ISO format (yyyy-mm-dd)
  const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const year = isoMatch[1];
    const monthIndex = parseInt(isoMatch[2], 10) - 1;
    const day = isoMatch[3];
    return `${day}-${MONTH_ABBREVIATIONS[monthIndex]}-${year}`;
  }
  
  return dateString;
};

/**
 * Formats a number as currency
 * @param value - Number or string to format
 * @returns Formatted currency string (e.g., "$50,000")
 */
export const formatCurrency = (value: any): string => {
  if (!value && value !== 0) return '';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    maximumFractionDigits: 0 
  }).format(num);
};

/**
 * Gets the appropriate CSS class for a status badge
 * @param status - Status string
 * @returns CSS class name
 */
export const getStatusClass = (status?: string): string => {
  if (!status) return '';
  const statusLower = status.toLowerCase();
  if (statusLower.includes('complete')) return STATUS_CLASSES.COMPLETED;
  if (statusLower.includes('progress')) return STATUS_CLASSES.IN_PROGRESS;
  return STATUS_CLASSES.NOT_STARTED;
};

/**
 * Inserts zero-width spaces into very long words to allow wrapping
 * @param text - Text to process
 * @param chunkSize - Maximum characters before inserting a break (default: 120)
 * @returns Processed text with zero-width spaces
 */
export const breakLongWords = (text: string | undefined, chunkSize = 120): string | undefined => {
  if (!text) return text;
  return text
    .split('\n')
    .map(line => line.replace(new RegExp(`([^\\s]{${chunkSize}})`, 'g'), `$1\u200b`))
    .join('\n');
};

/**
 * Gets the grid template columns based on section heading
 * @param heading - Section heading
 * @returns Grid template columns string
 */
export const getGridTemplate = (heading: string): string => {
  switch (heading) {
    case 'Action Plan':
      return '2fr 1fr 1fr 1fr';
    case 'Results & Lessons Learned':
      return '2fr 1fr';
    default:
      return '2fr 1fr 2fr';
  }
};

/**
 * Gets the appropriate CSS class for row type based on section heading
 * @param heading - Section heading
 * @returns CSS class name
 */
export const getRowClass = (heading: string): string => {
  switch (heading) {
    case 'Action Plan':
      return 'action-row';
    case 'Countermeasures':
      return 'counter-row';
    case 'Results & Lessons Learned':
      return 'lesson-row';
    default:
      return 'cause-row';
  }
};

/**
 * Validates if a field value is empty
 * @param value - Field value to check
 * @returns True if empty, false otherwise
 */
export const isFieldEmpty = (value: any): boolean => {
  return !value || (typeof value === 'string' && value.trim() === '');
};

/**
 * Gets the placeholder text for a specific field label
 * @param label - Field label
 * @param placeholders - Placeholders object from constants
 * @returns Placeholder text or empty string
 */
export const getPlaceholderForLabel = (label: string, placeholders: Record<string, string>): string => {
  const placeholderMap: Record<string, string> = {
    'Title': placeholders.TITLE,
    'Site/DC': placeholders.SITE_DC,
    'Tags (comma-separated)': placeholders.TAGS,
    'Date': placeholders.DATE,
    'Validated Savings ($, optional)': placeholders.VALIDATED_SAVINGS,
    'Background': placeholders.BACKGROUND,
    'Problem Statement': placeholders.PROBLEM_STATEMENT,
    'Description': placeholders.DESCRIPTION,
    'Goal Statement': placeholders.GOAL_STATEMENT,
    'Target Metric': placeholders.TARGET_METRIC,
    'Target Date': placeholders.TARGET_DATE,
    'Metric 1': placeholders.METRIC_1,
    'Metric 2 (optional)': placeholders.METRIC_2
  };
  
  return placeholderMap[label] || '';
};

/**
 * Checks if a field label indicates it's a metric field
 * @param label - Field label
 * @returns True if it's a metric field
 */
export const isMetricField = (label: string): boolean => {
  return label === 'Metric 1' || label === 'Metric 2 (optional)';
};

/**
 * Gets the unfavorable flag field label for a metric
 * @param metricLabel - Metric field label
 * @returns Unfavorable flag field label
 */
export const getUnfavorableFlagLabel = (metricLabel: string): string => {
  return metricLabel === 'Metric 1' ? 'Metric 1 Unfavorable' : 'Metric 2 Unfavorable';
};

/**
 * Extracts row data from section fields
 * @param fields - Array of fields
 * @param childLabelMap - Map of child label names to extract
 * @returns Array of row data objects
 */
export const extractRowData = (
  fields: any[],
  childLabelMap: Record<string, string>
): Record<string, string>[] => {
  return fields
    .filter(f => f.type === 'row')
    .map(row => {
      const result: Record<string, string> = {};
      Object.keys(childLabelMap).forEach(key => {
        const label = childLabelMap[key];
        result[key] = row.children?.find((c: any) => c.label === label)?.value || '';
      });
      return result;
    });
};
